/**
 * NovaAI — Workspace AI editor hub.
 *
 * - Provider setup lives in Admin → Ayarlar → NovaAI Sağlayıcıları
 * - This page is purely a chat workspace with a workspace-item library sidebar
 *   so the admin can browse their notes/writings/todos/documents/quotes and
 *   drop any item straight into the active conversation.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, Plus, Trash2, Send, Loader2, Settings, X, Paperclip,
  FileEdit, StickyNote, FolderArchive, Quote as QuoteIcon, CheckSquare,
  Download, Pencil, Check, Bot, User, Search,
} from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { WsPageTitle } from "./WorkspaceLayout";
import { Field } from "../../components/admin/Fields";

const KIND_ICON = {
  writings: FileEdit, notes: StickyNote, todos: CheckSquare,
  documents: FolderArchive, quotes: QuoteIcon,
};
const KIND_LABEL = {
  writings: "Yazılarım", notes: "Notlarım", todos: "To Do",
  documents: "Belgelerim", quotes: "Alıntılarım",
};
const LIBRARY_KINDS = ["writings", "notes", "todos", "documents", "quotes"];

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

/** Lightweight markdown-ish renderer — handles **bold**, bullet lists,
 *  blank-line paragraph breaks, and ``` fenced code blocks. */
function renderReply(text) {
  if (!text) return null;
  const blocks = text.split(/```/);
  return (
    <div className="space-y-2 text-sm leading-relaxed whitespace-pre-wrap">
      {blocks.map((b, i) => {
        if (i % 2 === 1) {
          return (
            <pre key={i} className="bg-slate-900 text-slate-100 rounded-lg p-3 overflow-x-auto text-xs">
              {b.replace(/^\w+\n/, "")}
            </pre>
          );
        }
        return (
          <div key={i}
            dangerouslySetInnerHTML={{
              __html: b
                .replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]))
                .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
                .replace(/`([^`]+)`/g, "<code class='bg-stone-100 text-rose-700 px-1 rounded text-xs'>$1</code>"),
            }}
          />
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Library — tabs that list workspace items so admin can drop them into chat
// ---------------------------------------------------------------------------
function LibraryPanel({ picked, onPick }) {
  const [kind, setKind] = useState("writings");
  const [q, setQ] = useState("");

  const { data = [], isLoading } = useQuery({
    queryKey: ["nova", "import", kind, q],
    queryFn: () => api.get(`/admin/nova/import/${kind}`, { params: { q } }).then((r) => r.data),
  });

  const isPicked = (item) => picked.some((p) => p.id === item.id && p.kind === item.kind);

  return (
    <aside className="bg-white rounded-2xl border border-indigo-100 shadow-sm overflow-hidden flex flex-col max-h-[75vh]"
           data-testid="nova-library-panel">
      <div className="px-3 py-2 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100 flex items-center gap-2">
        <Sparkles size={13} className="text-amber-600" />
        <span className="text-xs font-semibold text-amber-900 flex-1">Çalışma kütüphanem</span>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-stone-100 overflow-x-auto no-scrollbar">
        {LIBRARY_KINDS.map((k) => {
          const Ico = KIND_ICON[k];
          const active = kind === k;
          return (
            <button
              key={k}
              onClick={() => setKind(k)}
              className={`flex-1 min-w-[58px] py-2 px-1 text-[10px] font-semibold flex flex-col items-center justify-center gap-0.5 border-b-2 whitespace-nowrap transition ${
                active
                  ? "border-indigo-500 text-indigo-700 bg-indigo-50/40"
                  : "border-transparent text-stone-500 hover:bg-stone-50"
              }`}
              data-testid={`nova-lib-tab-${k}`}
              title={KIND_LABEL[k]}
            >
              <Ico size={14} />
              <span className="truncate max-w-full">{KIND_LABEL[k]}</span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="p-2 border-b border-stone-100">
        <div className="relative">
          <Search size={12} className="absolute left-2.5 top-2 text-stone-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder={`${KIND_LABEL[kind]} içinde ara...`}
                 className="h-7 text-xs pl-7"
                 data-testid="nova-lib-search" />
        </div>
      </div>

      {/* List */}
      <ul className="flex-1 overflow-y-auto" data-testid={`nova-lib-list-${kind}`}>
        {isLoading && (
          <li className="p-4 text-center">
            <Loader2 size={16} className="inline animate-spin text-indigo-600" />
          </li>
        )}
        {!isLoading && data.length === 0 && (
          <li className="p-6 text-center text-xs text-stone-400 italic">
            {q ? "Eşleşen kayıt yok." : `Henüz ${KIND_LABEL[kind].toLowerCase()} yok.`}
          </li>
        )}
        {!isLoading && data.map((item) => {
          const already = isPicked(item);
          const Ico = KIND_ICON[item.kind] || Paperclip;
          const subtitle =
            item.kind === "todos"
              ? [item.done ? "✔ tamamlandı" : "☐ açık", item.priority, item.due_date]
                  .filter(Boolean).join(" · ")
              : item.category;
          return (
            <li key={`${item.kind}:${item.id}`}>
              <button
                onClick={() => onPick(item)}
                className={`w-full text-left px-3 py-2 text-xs border-b border-stone-100 hover:bg-indigo-50 flex items-start gap-2 ${
                  already ? "bg-indigo-50/60" : ""
                }`}
                data-testid={`nova-lib-item-${item.id}`}
                title={already ? "Sohbetten çıkarmak için tıklayın" : "Sohbete ekle"}
              >
                <Ico size={12} className={`mt-0.5 shrink-0 ${already ? "text-indigo-600" : "text-stone-400"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-slate-900 font-medium truncate">
                    {item.label || "(başlıksız)"}
                  </div>
                  {subtitle && (
                    <div className="text-[10px] text-stone-500 truncate mt-0.5">{subtitle}</div>
                  )}
                </div>
                {already
                  ? <Check size={12} className="text-indigo-600 shrink-0 mt-0.5" />
                  : <Plus size={12} className="text-stone-400 shrink-0 mt-0.5" />}
              </button>
            </li>
          );
        })}
      </ul>

      <div className="px-3 py-2 border-t border-stone-100 bg-stone-50 text-[10px] text-stone-500">
        Bir öğeye tıklayın → aktif sohbete eklenir.
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Export reply back to workspace
// ---------------------------------------------------------------------------
function ExportDialog({ open, onClose, content }) {
  const [kind, setKind] = useState("notes");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [author, setAuthor] = useState("NovaAI");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const run = async () => {
    setBusy(true);
    try {
      await api.post("/admin/nova/export", {
        kind, title: title || "NovaAI çıktısı", body: content,
        category, author,
      });
      toast.success(`${KIND_LABEL[kind] || kind} olarak kaydedildi`);
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[90] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}
           data-testid="nova-export-modal">
        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Download size={16} className="text-indigo-600" /> Workspace'e aktar
        </h3>
        <div className="space-y-3">
          <Field label="Nereye kaydedilsin?">
            <select value={kind} onChange={(e) => setKind(e.target.value)}
                    className="w-full h-9 border rounded-md px-3 text-sm bg-white"
                    data-testid="nova-export-kind">
              <option value="notes">Not</option>
              <option value="writings">Yazı</option>
              <option value="quotes">Alıntı</option>
            </select>
          </Field>
          {kind !== "quotes" && (
            <Field label="Başlık">
              <Input value={title} onChange={(e) => setTitle(e.target.value)}
                     placeholder="(opsiyonel)" data-testid="nova-export-title" />
            </Field>
          )}
          {kind === "quotes" && (
            <Field label="Yazar / kaynak">
              <Input value={author} onChange={(e) => setAuthor(e.target.value)}
                     data-testid="nova-export-author" />
            </Field>
          )}
          <Field label="Kategori (ops.)">
            <Input value={category} onChange={(e) => setCategory(e.target.value)}
                   data-testid="nova-export-category" />
          </Field>
        </div>
        <div className="flex items-center gap-2 justify-end mt-4">
          <Button variant="ghost" onClick={onClose}>İptal</Button>
          <Button onClick={run} disabled={busy} data-testid="nova-export-confirm">
            {busy ? <Loader2 size={13} className="animate-spin mr-1" /> : <Download size={13} className="mr-1" />}
            Kaydet
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function Nova() {
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState("");
  const [attached, setAttached] = useState([]);
  const [sending, setSending] = useState(false);
  const [exportContent, setExportContent] = useState(null);
  const scrollerRef = useRef(null);

  const { data: providers = [] } = useQuery({
    queryKey: ["nova", "providers"],
    queryFn: () => api.get("/admin/nova/providers").then((r) => r.data),
  });

  const { data: sessions = [] } = useQuery({
    queryKey: ["nova", "sessions"],
    queryFn: () => api.get("/admin/nova/sessions").then((r) => r.data),
  });

  const { data: messages = [], isLoading: msgLoading } = useQuery({
    queryKey: ["nova", "messages", activeId],
    queryFn: () => api.get(`/admin/nova/sessions/${activeId}/messages`).then((r) => r.data),
    enabled: !!activeId,
  });

  // Auto-select first session on load
  useEffect(() => {
    if (!activeId && sessions.length > 0) setActiveId(sessions[0].id);
  }, [sessions, activeId]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  const activeProvider = useMemo(() => {
    const s = sessions.find((x) => x.id === activeId);
    if (s?.provider_id) return providers.find((p) => p.id === s.provider_id);
    return providers.find((p) => p.is_default) || providers[0];
  }, [providers, sessions, activeId]);

  const newSession = async () => {
    try {
      const { data: s } = await api.post("/admin/nova/sessions", { title: "Yeni sohbet" });
      qc.invalidateQueries({ queryKey: ["nova", "sessions"] });
      setActiveId(s.id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const renameSession = async (s) => {
    const t = window.prompt("Sohbet başlığı:", s.title || "");
    if (t == null) return;
    try {
      await api.put(`/admin/nova/sessions/${s.id}`, { title: t });
      qc.invalidateQueries({ queryKey: ["nova", "sessions"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteSession = async (s) => {
    if (!window.confirm(`"${s.title}" sohbeti ve tüm mesajları silinecek. Onaylıyor musunuz?`)) return;
    try {
      await api.delete(`/admin/nova/sessions/${s.id}`);
      qc.invalidateQueries({ queryKey: ["nova", "sessions"] });
      if (activeId === s.id) setActiveId(null);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setSessionProvider = async (pid) => {
    if (!activeId) return;
    try {
      await api.put(`/admin/nova/sessions/${activeId}`, { provider_id: pid || null });
      qc.invalidateQueries({ queryKey: ["nova", "sessions"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const send = async () => {
    const content = draft.trim();
    if (!content) return;
    if (providers.length === 0) {
      toast.error("Önce Ayarlar → NovaAI Sağlayıcıları ekranından bir sağlayıcı ekleyin");
      return;
    }
    let sid = activeId;
    if (!sid) {
      try {
        const { data: s } = await api.post("/admin/nova/sessions", { title: content.slice(0, 60) });
        sid = s.id;
        setActiveId(s.id);
        qc.invalidateQueries({ queryKey: ["nova", "sessions"] });
      } catch (e) { toast.error(errMsg(e)); return; }
    }
    setSending(true);
    try {
      await api.post(`/admin/nova/sessions/${sid}/chat`, {
        content, attachments: attached, system: "", temperature: 0.7,
      });
      setDraft("");
      setAttached([]);
      qc.invalidateQueries({ queryKey: ["nova", "messages", sid] });
      qc.invalidateQueries({ queryKey: ["nova", "sessions"] });
    } catch (e) { toast.error(errMsg(e)); }
    setSending(false);
  };

  const pickAttachment = (item) => {
    setAttached((cur) => {
      const exists = cur.find((p) => p.id === item.id && p.kind === item.kind);
      if (exists) {
        toast("Sohbetten kaldırıldı", { description: item.label });
        return cur.filter((p) => !(p.id === item.id && p.kind === item.kind));
      }
      toast.success("Sohbete eklendi", { description: item.label });
      return [...cur, { id: item.id, kind: item.kind, title: item.label }];
    });
  };

  const removeMessage = async (m) => {
    if (!window.confirm("Mesaj silinsin mi?")) return;
    try {
      await api.delete(`/admin/nova/sessions/${activeId}/messages/${m.id}`);
      qc.invalidateQueries({ queryKey: ["nova", "messages", activeId] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="ws-nova">
      <WsPageTitle
        icon={Sparkles}
        title="NovaAI"
        sub="Kendi API anahtarlarınla her modeli kullanabildiğin, workspace'ten içerik çekip çıktısını geri aktarabildiğin AI editörün."
        action={
          <>
            <Link to="/admin/workspace/ayarlar?tab=nova" data-testid="nova-open-settings">
              <Button variant="outline">
                <Settings size={13} className="mr-1" />
                Sağlayıcılar ({providers.length})
              </Button>
            </Link>
            <Button onClick={newSession} data-testid="nova-new-session">
              <Plus size={13} className="mr-1" />Yeni sohbet
            </Button>
          </>
        }
      />

      {providers.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 flex items-start gap-3"
             data-testid="nova-empty-providers">
          <Bot size={28} className="text-amber-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900">Hiç AI sağlayıcın yok.</p>
            <p className="text-xs text-amber-800 mt-1">
              Sağlayıcı eklemek için <strong>Workspace → Ayarlar → NovaAI Sağlayıcıları</strong> sekmesine git.
              Oradan OpenAI, Anthropic Claude, Google Gemini, OpenRouter veya özel bir sağlayıcı tanımlayıp
              kendi API anahtarınla NovaAI'yı kullanmaya başlayabilirsin.
            </p>
            <Link to="/admin/workspace/ayarlar?tab=nova">
              <Button size="sm" className="mt-2" data-testid="nova-empty-add">
                <Settings size={13} className="mr-1" />Ayarlar'a git
              </Button>
            </Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_320px] gap-3 min-h-[75vh]">
        {/* Sessions sidebar */}
        <aside className="bg-white rounded-2xl border border-indigo-100 shadow-sm overflow-hidden flex flex-col max-h-[75vh]">
          <div className="px-3 py-2 bg-indigo-50 border-b border-indigo-100 text-xs font-semibold text-indigo-800">
            {sessions.length} sohbet
          </div>
          <ul className="flex-1 overflow-y-auto" data-testid="nova-sessions-list">
            {sessions.length === 0 && (
              <li className="p-6 text-xs text-stone-400 italic text-center">
                Henüz sohbet yok. "Yeni sohbet"e bas.
              </li>
            )}
            {sessions.map((s) => (
              <li key={s.id} className="group">
                <button
                  onClick={() => setActiveId(s.id)}
                  className={`w-full text-left px-3 py-2.5 border-b border-stone-100 flex items-start gap-2 ${
                    activeId === s.id ? "bg-indigo-50 border-l-4 border-l-indigo-500" : "hover:bg-stone-50"
                  }`}
                  data-testid={`nova-session-${s.id}`}
                >
                  <Sparkles size={13} className={`mt-0.5 shrink-0 ${activeId === s.id ? "text-indigo-600" : "text-stone-400"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-slate-900 truncate">{s.title || "Yeni sohbet"}</div>
                    <div className="text-[10px] text-stone-500 mt-0.5">{fmtDate(s.updated_at)}</div>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5">
                    <button onClick={(e) => { e.stopPropagation(); renameSession(s); }}
                            className="p-1 text-stone-400 hover:text-indigo-700 rounded hover:bg-white"
                            data-testid={`nova-session-rename-${s.id}`}>
                      <Pencil size={11} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); deleteSession(s); }}
                            className="p-1 text-stone-400 hover:text-red-600 rounded hover:bg-white"
                            data-testid={`nova-session-del-${s.id}`}>
                      <Trash2 size={11} />
                    </button>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Chat panel */}
        <section className="bg-white rounded-2xl border border-indigo-100 shadow-sm overflow-hidden flex flex-col max-h-[75vh]">
          {/* Provider selector header */}
          <div className="px-4 py-3 border-b border-indigo-100 bg-gradient-to-r from-indigo-50/60 to-violet-50/60 flex items-center gap-3 flex-wrap">
            <Bot size={16} className="text-indigo-600" />
            <select
              value={activeProvider?.id || ""}
              onChange={(e) => setSessionProvider(e.target.value)}
              disabled={!activeId || providers.length === 0}
              className="text-sm h-8 border rounded-md px-2 bg-white max-w-[300px]"
              data-testid="nova-provider-select"
            >
              {providers.length === 0 && <option value="">— sağlayıcı yok —</option>}
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} {p.is_default ? "★" : ""} ({p.model})
                </option>
              ))}
            </select>
            <span className="flex-1" />
            {activeProvider && !activeProvider.has_key && (
              <span className="text-[11px] text-red-600 font-semibold">⚠️ API key eksik</span>
            )}
          </div>

          {/* Messages */}
          <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 bg-gradient-to-b from-stone-50 to-white"
               data-testid="nova-messages">
            {!activeId ? (
              <div className="h-full flex items-center justify-center text-center text-stone-400 italic">
                <div>
                  <Sparkles size={32} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Soldan bir sohbet seçin veya yeni oluşturun</p>
                </div>
              </div>
            ) : msgLoading ? (
              <div className="h-full flex items-center justify-center text-stone-400">
                <Loader2 size={24} className="animate-spin" />
              </div>
            ) : messages.length === 0 && !sending ? (
              <div className="h-full flex items-center justify-center text-center text-stone-400 italic">
                <div>
                  <Sparkles size={32} className="mx-auto mb-2 opacity-40 text-amber-500" />
                  <p className="text-sm">NovaAI dinliyor. Sağdaki kütüphaneden içerik ekleyip sor.</p>
                </div>
              </div>
            ) : (
              <ul className="space-y-4">
                {messages.map((m) => (
                  <li key={m.id} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}
                      data-testid={`nova-msg-${m.role}-${m.id}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                      m.role === "user" ? "bg-indigo-600 text-white" : "bg-amber-100 text-amber-700"
                    }`}>
                      {m.role === "user" ? <User size={15} /> : <Bot size={15} />}
                    </div>
                    <div className={`max-w-[80%] group ${m.role === "user" ? "text-right" : ""}`}>
                      <div className={`rounded-2xl px-4 py-2.5 ${
                        m.role === "user" ? "bg-indigo-600 text-white" : "bg-white border border-stone-200"
                      }`}>
                        {m.attachments?.length > 0 && (
                          <div className="mb-2 flex flex-wrap gap-1">
                            {m.attachments.map((a, i) => {
                              const Ico = KIND_ICON[a.kind] || Paperclip;
                              return (
                                <span key={i} className="text-[10px] bg-black/20 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                                  <Ico size={9} />{a.title || a.id}
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {m.role === "user" ? (
                          <p className="whitespace-pre-wrap text-sm">{m.content}</p>
                        ) : (
                          renderReply(m.content)
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-stone-400 mt-1 opacity-0 group-hover:opacity-100">
                        <span>{fmtDate(m.created_at)}</span>
                        {m.role === "assistant" && (
                          <>
                            <button onClick={() => { navigator.clipboard.writeText(m.content); toast.success("Kopyalandı"); }}
                                    className="hover:text-indigo-600"
                                    data-testid={`nova-msg-copy-${m.id}`}>
                              Kopyala
                            </button>
                            <button onClick={() => setExportContent(m.content)}
                                    className="hover:text-emerald-600"
                                    data-testid={`nova-msg-export-${m.id}`}>
                              Workspace'e aktar
                            </button>
                          </>
                        )}
                        <button onClick={() => removeMessage(m)} className="hover:text-red-600">Sil</button>
                      </div>
                    </div>
                  </li>
                ))}
                {sending && (
                  <li className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
                      <Bot size={15} />
                    </div>
                    <div className="rounded-2xl px-4 py-2.5 bg-white border border-stone-200">
                      <Loader2 size={16} className="animate-spin text-indigo-600" />
                    </div>
                  </li>
                )}
              </ul>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-indigo-100 p-3 bg-white">
            {attached.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2" data-testid="nova-attached-chips">
                {attached.map((a) => {
                  const Ico = KIND_ICON[a.kind] || Paperclip;
                  return (
                    <span key={`${a.kind}:${a.id}`}
                          className="inline-flex items-center gap-1 text-[11px] bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-full px-2 py-0.5">
                      <Ico size={10} />
                      {a.title}
                      <button onClick={() => setAttached((cur) => cur.filter((p) => !(p.id === a.id && p.kind === a.kind)))}
                              className="hover:text-red-600"
                              data-testid={`nova-attached-remove-${a.id}`}>
                        <X size={9} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            <div className="flex items-end gap-2">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault(); send();
                  }
                }}
                placeholder={activeId ? "NovaAI'ya sor... (Ctrl+Enter = gönder)" : "Yeni sohbet başlatmak için yazın..."}
                rows={2}
                className="flex-1 text-sm resize-none"
                data-testid="nova-composer-input"
              />
              <Button onClick={send} disabled={sending || !draft.trim()}
                      data-testid="nova-composer-send">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </Button>
            </div>
          </div>
        </section>

        {/* Workspace library sidebar */}
        <LibraryPanel picked={attached} onPick={pickAttachment} />
      </div>

      <ExportDialog
        open={!!exportContent}
        onClose={() => setExportContent(null)}
        content={exportContent || ""}
      />
    </div>
  );
}
